
import { supabase } from "@/integrations/supabase/client";
import { AccountPlan } from "@/types/growth.types";

// Fetch a specific account plan by ID
export const fetchAccountPlanById = async (id: string): Promise<AccountPlan> => {
  const { data, error } = await supabase
    .from('account_plans')
    .select(`
      *,
      organization:organization_id (id, name),
      created_by_user:created_by (id, first_name, last_name)
    `)
    .eq('id', id)
    .single();
    
  if (error) throw error;
  return data as AccountPlan;
};

// Create a new account plan
export const createAccountPlan = async (planData: Partial<AccountPlan>): Promise<AccountPlan> => {
  // Ensure required fields are present
  if (!planData.organization_id) {
    throw new Error("Organization ID is required");
  }
  
  if (!planData.plan_year) {
    throw new Error("Plan year is required");
  }
  
  const { data, error } = await supabase
    .from('account_plans')
    .insert({
      organization_id: planData.organization_id,
      plan_year: planData.plan_year,
      plan_quarter: planData.plan_quarter,
      revenue_target: planData.revenue_target,
      growth_target_percentage: planData.growth_target_percentage,
      priority_level: planData.priority_level,
      strategic_goals: planData.strategic_goals,
      whitespace_assessment: planData.whitespace_assessment,
      challenges: planData.challenges,
      opportunities_summary: planData.opportunities_summary,
      key_initiatives: planData.key_initiatives,
      success_metrics: planData.success_metrics,
      created_by: planData.created_by,
      status: planData.status || 'draft'
    })
    .select()
    .single();
    
  if (error) throw error;
  return data as AccountPlan;
};

// Update an account plan
export const updateAccountPlan = async (
  id: string,
  planData: Partial<AccountPlan>
): Promise<AccountPlan> => {
  const { data, error } = await supabase
    .from('account_plans')
    .update(planData)
    .eq('id', id)
    .select()
    .single();
    
  if (error) throw error;
  return data as AccountPlan;
};

// Delete an account plan
export const deleteAccountPlan = async (id: string): Promise<boolean> => {
  const { error } = await supabase
    .from('account_plans')
    .delete()
    .eq('id', id);
    
  if (error) throw error;
  return true;
};

// Fetch all account plans
export const fetchAccountPlans = async (): Promise<AccountPlan[]> => {
  const { data, error } = await supabase
    .from('account_plans')
    .select(`
      *,
      organization:organization_id (id, name),
      created_by_user:created_by (id, first_name, last_name)
    `)
    .order('plan_year', { ascending: false })
    .order('plan_quarter', { ascending: false });
    
  if (error) throw error;
  return data as AccountPlan[];
};

// Fetch account plans for a specific organization
export const fetchOrganizationAccountPlans = async (organizationId: string): Promise<AccountPlan[]> => {
  const { data, error } = await supabase
    .from('account_plans')
    .select(`
      *,
      organization:organization_id (id, name),
      created_by_user:created_by (id, first_name, last_name)
    `)
    .eq('organization_id', organizationId)
    .order('plan_year', { ascending: false })
    .order('plan_quarter', { ascending: false });
    
  if (error) throw error;
  return data as AccountPlan[];
};

// Get plans for the current (or specified) quarter
export const getCurrentQuarterPlans = async (year?: number, quarter?: number): Promise<AccountPlan[]> => {
  // If no year or quarter specified, use current
  const now = new Date();
  const currentYear = year || now.getFullYear();
  const currentQuarter = quarter || Math.floor(now.getMonth() / 3) + 1;
  
  const { data, error } = await supabase
    .from('account_plans')
    .select(`
      *,
      organization:organization_id (id, name),
      created_by_user:created_by (id, first_name, last_name)
    `)
    .eq('plan_year', currentYear)
    .eq('plan_quarter', currentQuarter);
    
  if (error) throw error;
  return data as AccountPlan[];
};

// Generate quarterly plans for active clients
export const generateQuarterlyPlans = async (
  options: { year: number, quarter: number }
): Promise<{ success: boolean, count: number }> => {
  // Call the database function to generate quarterly plans
  const { data, error } = await supabase
    .rpc('generate_quarterly_account_plans', {
      year_to_generate: options.year,
      quarter_to_generate: options.quarter
    });
    
  if (error) throw error;
  
  // Get count of plans generated
  const { count, error: countError } = await supabase
    .from('account_plans')
    .select('*', { count: 'exact' })
    .eq('plan_year', options.year)
    .eq('plan_quarter', options.quarter);
    
  if (countError) throw countError;
  
  return { 
    success: true, 
    count: count || 0
  };
};

// Get account planning statistics
export const getAccountPlanningStats = async () => {
  // Get total plans
  const { count: totalPlans, error: plansError } = await supabase
    .from('account_plans')
    .select('*', { count: 'exact' });
    
  if (plansError) throw plansError;
  
  // Get completed plans
  const { count: completedPlans, error: completedError } = await supabase
    .from('account_plans')
    .select('*', { count: 'exact' })
    .eq('status', 'completed');
    
  if (completedError) throw completedError;
  
  // Get plans in progress
  const { count: inProgressPlans, error: inProgressError } = await supabase
    .from('account_plans')
    .select('*', { count: 'exact' })
    .eq('status', 'in_progress');
    
  if (inProgressError) throw inProgressError;
  
  // Get draft plans
  const { count: draftPlans, error: draftError } = await supabase
    .from('account_plans')
    .select('*', { count: 'exact' })
    .eq('status', 'draft');
    
  if (draftError) throw draftError;
  
  return {
    totalPlans: totalPlans || 0,
    completedPlans: completedPlans || 0,
    pendingPlans: inProgressPlans || 0,
    draftPlans: draftPlans || 0
  };
};
